import { create } from 'zustand';

/**
 * Replaces `AppraisalDataService`'s three `BehaviorSubject`s.
 *
 * A `BehaviorSubject` is a value plus a subscription, which is precisely what
 * a Zustand store is - so the mapping is one for one:
 *
 *   rowData$      .subscribe(fn)  ->  useAppraisalStore(s => s.rowData)
 *   setRowData(x)                 ->  useAppraisalStore.getState().setRowData(x)
 *   getRowData()                  ->  useAppraisalStore.getState().rowData
 *
 * Reading outside React (grid callbacks, cell renderers, export helpers) uses
 * `.getState()`, which is the direct equivalent of the old synchronous getters
 * and does not subscribe the caller to changes.
 *
 * The HTTP calls that used to hang off this service moved to
 * `src/core/api/appraisal.api.ts`; this file is state only.
 */

export interface AppraisalState {
  /** The activity currently open in the shell - drives the sidebar activity card. */
  rowData: any | null;
  /** Every row behind the current grid view, kept for cross-row export and validation. */
  allRowsData: any | null;
  /** AG Grid column/filter/sort state, so a screen can be returned to as it was left. */
  gridState: any | null;

  setRowData: (data: any) => void;
  setAllRowsData: (data: any) => void;
  setGridStateData: (data: any) => void;
}

export const useAppraisalStore = create<AppraisalState>((set) => ({
  rowData: null,
  allRowsData: null,
  gridState: null,

  setRowData: (data) => set({ rowData: data }),
  setAllRowsData: (data) => set({ allRowsData: data }),
  setGridStateData: (data) => set({ gridState: data })
}));

/** Non-reactive reads, for the callback contexts AG Grid hands us. */
export const appraisalStore = {
  getRowData: () => useAppraisalStore.getState().rowData,
  getAllRowsData: () => useAppraisalStore.getState().allRowsData,
  getGridStateData: () => useAppraisalStore.getState().gridState,
  setRowData: (data: any) => useAppraisalStore.getState().setRowData(data),
  setAllRowsData: (data: any) => useAppraisalStore.getState().setAllRowsData(data),
  setGridStateData: (data: any) => useAppraisalStore.getState().setGridStateData(data)
};
